import firebase from 'firebase/app';
import 'firebase/storage';

/**
 * Gets all the documents in iconNameComponentCategories collection
 *
 * @export
 * @return {Promise} - Promise which succeeds once the request is successful
 */
export async function getIconNameComponentCategories() {
    var db = firebase.firestore();
    return await db.collection('iconNameComponentCategories').get();
}

/**
 * Gets the details of the icon document with uid as iconUid
 *
 * @export
 * @param {String} iconUid - UID of the icon document
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the document data object.
 */
export async function getIconDetails(iconUid) {
    if (iconUid) {
        var db = firebase.firestore();
        var doc = await db.collection('icons').doc(iconUid).get();
        if (doc.exists) {
            return {
                ...doc.data(),
                created_at: doc.get('created_at').toDate(),
                last_updated_at: doc.get('last_updated_at').toDate(),
                id: doc.id,
            };
        } else {
            throw new Error('Icon with this ID doesnt exist');
        }
    } else {
        throw new Error('Invalid Icon ID');
    }
}

/**
 * Creates a new icon
 * @export
 * @param {Object} name - Name object of the icon
 * @param {File} iconFile - Svg file of the icon
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the newly created icon's uid.
 */
export async function createIcon(name, iconFile, userId) {
    var combinedName = getCombinedName(name);
    // Check if icon name is unique
    await isIconNameUnique(combinedName);
    // Create icon record on firebase
    var recordId = await createIconRecordOnDb(name, combinedName, userId);
    // upload image to firebase storage
    var fileUrl = await uploadIconFile(combinedName, iconFile);
    // Add url to uploaded image to created record
    await addFileUrlForIcon(recordId, userId, fileUrl);
    return recordId;
}

/**
 * Updates an already created icon
 *
 * @export
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} iconId - UID of the icon document
 * @param {File} iconFile - Svg file of the icon
 * @param {String} combinedName - Combined name of the icon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} Promise which succeeds once the request is successful.
 */
export async function updateIcon(userId, iconId, iconFile, combinedName) {
    // upload image to firebase storage
    var fileUrl = await uploadIconFile(combinedName, iconFile);
    // Update url of uploaded image to created record
    return await addFileUrlForIcon(iconId, userId, fileUrl);
}

/**
 * Returns the combined icon name given the icon Name object
 *
 * @export
 * @param {Object} name - Name object of the icon. Should contain 3 keys : icon_category, icon_size, icon_name
 * @return {String} - The combined name string of the icon
 */
export function getCombinedName(name) {
    return `${name.icon_category}_${name.icon_size}_${name.icon_name}`;
}

/**
 * Checks if the icon with the given name already exists or not
 *
 * @param {String} combinedName - Combined name of the icon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} - Promise which succeeds if the icon name is unique else throws an error
 */
async function isIconNameUnique(combinedName) {
    // check on firebase if icon with same name already exists
    var db = firebase.firestore();
    var querySnapshot = await db
        .collection('icons')
        .where('combined_name', '==', combinedName)
        .limit(1)
        .get();
    if (querySnapshot.size > 0) {
        throw new Error('Icon with the same name already exists');
    } else {
        return;
    }
}

/**
 * Created document for an icon on the database
 *
 * @param {Object} name - Name object of the icon
 * @param {String} combinedName - Combined name of the icon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds when the icon is successfully created, else throws an error.
 */
async function createIconRecordOnDb(name, combinedName, userId) {
    var db = firebase.firestore();
    var iconsCollectionRef = db.collection('icons');

    var iconObj = {
        name: name,
        combined_name: combinedName,
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: userId,
        last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        last_updated_by: userId,
    };

    const docRef = await iconsCollectionRef.add(iconObj);
    return docRef.id;
}

/**
 * Updates icon document's image_url field
 *
 * @param {String} recordId - UID of the icon document
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} fileUrl - Url of the uploaded file
 * @return {Promise} - Promise that succeeds when the updation of the field is successful.
 */
async function addFileUrlForIcon(recordId, userId, fileUrl) {
    var db = firebase.firestore();
    var iconsCollectionRef = db.collection('icons');

    return await iconsCollectionRef.doc(recordId).update({
        img_url: fileUrl,
        last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        last_updated_by: userId,
    });
}

/**
 * Uploads an icon file to storage and returns url of the uploaded file
 *
 * @param {String} combinedIconName - Combined name of the icon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {File} file - Svg file of the icon
 * @return {Promise} - Promise that succeeds once the uploading of the icon file is successful. The promise contains the download url of the file.
 */
async function uploadIconFile(combinedIconName, file) {
    // Identifier could be default or region identifier or event identifier
    const storage = firebase.storage();
    var iconsRootFolderRef = storage.ref('coffer/icons');
    var iconImageRef = iconsRootFolderRef.child(`${combinedIconName}.svg`);
    const maxAge = 90 * 24 * 60 * 60; // days * hours * minutes * seconds
    var metadata = {
        cacheControl: `max-age=${maxAge}`,
    };
    await iconImageRef.put(file, metadata);
    const url = await iconImageRef.getDownloadURL();
    return url;
}
